/**
 * DashboardWizardDialog - one-shot AI-generated dashboard.
 *
 * User picks 2-4 datasets, describes what they want to see, and the wizard emits a
 * complete dashboard blueprint (widgets + layouts + federated_spec) that can be created
 * with one click. Backed by POST /api/ai/dashboard-wizard.
 */
import React, { useEffect, useState } from "react";
import { Wand2, Sparkles, Loader2, AlertCircle, CheckCircle2, ArrowRight, LayoutDashboard, Pencil, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { DashboardChatPanel } from "@/components/bi/DashboardChatPanel";

const EXAMPLE_PROMPTS = [
  "Show a Q4 sales performance review with KPIs, a revenue-by-region chart, and a CRM-vs-ERP comparison.",
  "Executive overview: pipeline health from CRM, actual revenue from ERP, and conversion rate by region.",
  "Product managers view: top-selling categories, month-over-month growth, and lead-to-sale funnel.",
];

export function DashboardWizardDialog({ open, onOpenChange }) {
  const nav = useNavigate();
  const [datasets, setDatasets] = useState([]);
  const [pickedIds, setPickedIds] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [chatMode, setChatMode] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/datasets").then(({ data }) => setDatasets(data || [])).catch(() => setDatasets([]));
  }, [open]);

  const reset = () => {
    setPickedIds([]); setPrompt(""); setName(""); setResult(null); setError(null); setChatMode(false);
  };

  const toggle = (id) => setPickedIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const generate = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const { data } = await api.post("/ai/dashboard-wizard", {
        prompt, dataset_ids: pickedIds,
        dashboard_name: name || null,
      });
      setResult(data);
      if (!name) setName(data.name || "");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Wizard failed");
    } finally { setBusy(false); }
  };

  const createDashboard = async () => {
    if (!result) return;
    setCreating(true);
    let created = null;
    try {
      const { data: d } = await api.post("/dashboards", {
        name: name || result.name,
        description: result.description || "",
      });
      created = d;
      await api.put(`/dashboards/${d.id}`, {
        name: name || result.name,
        description: result.description || "",
        widgets: result.widgets,
      });
      toast.success("Dashboard created");
      onOpenChange(false);
      reset();
      nav(`/builder/${d.id}`);
    } catch (e) {
      // If we created the shell but failed to attach widgets, don't leave orphans behind.
      if (created?.id) {
        try { await api.delete(`/dashboards/${created.id}`); } catch (err) { /* best-effort */ }
      }
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const canRun = pickedIds.length >= 1 && pickedIds.length <= 4 && prompt.trim().length > 8 && !busy;
  const badFedCount = (result?.validations || []).filter((v) => !v.validated).length;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[color:var(--brand-mint)]" />
            Cross-source Dashboard Wizard
          </DialogTitle>
          <DialogDescription>
            Pick your datasets, describe the story you want to tell, and let AI stitch a complete dashboard — KPIs, charts, and a governed cross-source view — in one shot.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Datasets (pick 1-4)</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-1.5 max-h-40 overflow-auto rounded-lg border border-[color:var(--glass-border)] p-2 bg-[color:var(--surface)]">
                {datasets.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 text-xs py-0.5 cursor-pointer hover:text-[color:var(--ink)]">
                    <Checkbox
                      data-testid={`wiz-ds-${d.id}`}
                      checked={pickedIds.includes(d.id)}
                      onCheckedChange={() => toggle(d.id)}
                    />
                    <span className="truncate">{d.name}</span>
                    {d.mode === "live" && <Badge variant="outline" className="text-[9px] py-0 h-4">live</Badge>}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-[color:var(--ink-3)]">What should this dashboard show?</Label>
                <div className="flex gap-1">
                  {EXAMPLE_PROMPTS.map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPrompt(p)}
                      className="text-[10px] px-1.5 py-0.5 rounded-full border border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:border-[color:var(--brand-ocean)]"
                    >Example {i + 1}</button>
                  ))}
                </div>
              </div>
              <Textarea
                data-testid="wiz-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="h-24 bg-[color:var(--surface)] text-sm"
                placeholder="e.g. Executive overview: pipeline health from CRM, actual revenue from ERP, conversion rate by region…"
              />
            </div>

            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Dashboard name (optional)</Label>
              <Input
                data-testid="wiz-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5"
                placeholder="Auto-generated if empty"
              />
            </div>

            {error && (
              <div className="text-[11px] text-[color:var(--danger)] flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />{error}
              </div>
            )}
          </div>
        )}

        {result && !chatMode && (
          <div className="space-y-3">
            <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
              <div className="flex items-center gap-2 mb-1">
                <LayoutDashboard className="w-4 h-4 text-[color:var(--brand-ocean)]" />
                <div className="font-display text-sm text-[color:var(--ink)] flex-1">{name || result.name}</div>
                <Button data-testid="wiz-chat-mode" size="sm" variant="outline" onClick={() => setChatMode(true)} className="text-[11px] h-7">
                  <MessageCircle className="w-3 h-3 mr-1" />Chat to refine
                </Button>
              </div>
              <p className="text-[11px] text-[color:var(--ink-3)] mb-3">{result.description}</p>
              <div className="grid grid-cols-2 gap-1.5">
                {(result.widgets || []).map((w, i) => (
                  <WidgetPreviewCard
                    key={w.id || i}
                    idx={i}
                    widget={w}
                    datasetIds={pickedIds}
                    onRefined={(updated) => setResult((cur) => cur ? {
                      ...cur,
                      widgets: cur.widgets.map((x, xi) => xi === i ? updated : x),
                    } : cur)}
                  />
                ))}
              </div>
            </div>

            {(result.validations || []).length > 0 && (
              <div className={"rounded-md p-2 text-[11px] " + (badFedCount === 0 ? "bg-[color:rgba(0,194,168,0.06)] border border-[color:var(--success)]/40" : "bg-[color:rgba(232,90,90,0.06)] border border-[color:var(--danger)]/40")}>
                {badFedCount === 0 ? (
                  <div className="flex items-center gap-1 text-[color:var(--success)]">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    All {result.validations.length} cross-source widget(s) dry-run cleanly.
                  </div>
                ) : (
                  <div className="text-[color:var(--danger)]">
                    <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                    {badFedCount} of {result.validations.length} cross-source widget(s) failed to dry-run:
                    <ul className="mt-1 ml-4 list-disc">
                      {result.validations.filter((v) => !v.validated).map((v, i) => (
                        <li key={i}>{v.title}: {v.error}</li>
                      ))}
                    </ul>
                    <p className="text-[10px] mt-1 opacity-80">You can still create the dashboard and fix them manually in the Builder.</p>
                  </div>
                )}
              </div>
            )}

            {(result.repaired || []).length > 0 && (
              <div data-testid="wiz-repaired" className="rounded-md bg-[color:rgba(43,78,255,0.06)] border border-[color:var(--brand-ocean)]/30 p-2 text-[10px]">
                <div className="text-[color:var(--brand-ocean)] font-medium mb-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />Auto-repaired {result.repaired.length} reference(s)
                </div>
                <ul className="font-mono space-y-0.5 text-[color:var(--ink-2)]">
                  {result.repaired.slice(0, 8).map((r, i) => (
                    <li key={i}>
                      <span className="text-[color:var(--ink-3)]">{r.where}:</span>{" "}
                      <span className="text-[color:var(--danger)] line-through">{r.from}</span> → <span className="text-[color:var(--success)]">{r.to}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {result && chatMode && (
          <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] overflow-hidden h-[420px] flex flex-col">
            <div className="px-3 py-2 border-b border-[color:var(--glass-border)] flex items-center gap-2 shrink-0">
              <MessageCircle className="w-4 h-4 text-[color:var(--brand-ocean)]" />
              <div className="flex-1 text-xs font-medium text-[color:var(--ink-2)]">Refine &ldquo;{name || result.name}&rdquo;</div>
              <Badge variant="outline" className="text-[9px] py-0 h-4">{(result.widgets || []).length} widget{(result.widgets || []).length === 1 ? "" : "s"}</Badge>
              <Button data-testid="wiz-chat-exit" size="sm" variant="ghost" onClick={() => setChatMode(false)} className="h-6 text-[11px]">
                Back to preview
              </Button>
            </div>
            <DashboardChatPanel
              datasetIds={pickedIds}
              currentWidgets={result.widgets || []}
              onWidgetsUpdate={(nextWidgets, meta) => setResult((cur) => cur ? {
                ...cur,
                widgets: nextWidgets,
                validations: meta?.validations || cur.validations,
                repaired: [...(cur.repaired || []), ...(meta?.repaired || [])],
              } : cur)}
              placeholderPrompts={[
                "Add a KPI card for total revenue",
                "Convert the second chart to a line chart",
                "Remove the perspective widget",
                "Add a top-10 categories table",
              ]}
              compact
              className="flex-1"
            />
          </div>
        )}

        <DialogFooter className="gap-1.5">
          <Button variant="outline" onClick={() => { onOpenChange(false); reset(); }}>Cancel</Button>
          {!result && (
            <Button data-testid="wiz-generate" onClick={generate} disabled={!canRun}>
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Generate dashboard
            </Button>
          )}
          {result && (
            <>
              <Button data-testid="wiz-restart" variant="outline" onClick={reset}>Start over</Button>
              <Button data-testid="wiz-create" onClick={createDashboard} disabled={creating} className="bg-[color:var(--brand-mint)] text-white hover:brightness-95">
                {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Create dashboard <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WidgetPreviewCard({ idx, widget, datasetIds, onRefined }) {
  const [open, setOpen] = React.useState(false);
  const [instruction, setInstruction] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const refine = async () => {
    if (!instruction.trim()) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/ai/refine-widget", {
        widget, instruction, dataset_ids: datasetIds,
      });
      onRefined(data.widget);
      toast.success("Widget refined");
      setOpen(false); setInstruction("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Refine failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div
        data-testid={`wiz-widget-${idx}`}
        className="rounded-md bg-[color:var(--surface)] border border-[color:var(--glass-border)] px-2 py-1.5 flex items-center gap-2 group"
      >
        <Badge variant="outline" className="text-[10px] py-0 h-4 uppercase font-mono">{widget.type}</Badge>
        <span className="text-xs text-[color:var(--ink-2)] truncate flex-1">{widget.title}</span>
        {widget.federated_spec && (
          <Badge variant="outline" className="text-[9px] py-0 h-4 text-[color:var(--brand-mint)] border-[color:var(--brand-mint)]/40">federated</Badge>
        )}
        <Button
          data-testid={`wiz-widget-refine-${idx}`}
          size="icon"
          variant="ghost"
          onClick={() => setOpen(true)}
          className="h-6 w-6 opacity-40 group-hover:opacity-100 transition-opacity"
          title="Refine this widget"
        >
          <Pencil className="w-3 h-3" />
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Pencil className="w-4 h-4 text-[color:var(--brand-ocean)]" />
              Refine: <span className="text-[color:var(--ink-2)]">{widget.title}</span>
            </DialogTitle>
            <DialogDescription>
              Describe the tweak you want. The AI will edit only this widget and preserve everything else on the dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea
              data-testid={`wiz-refine-instruction-${idx}`}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              className="h-24 bg-[color:var(--surface)] text-sm"
              placeholder="e.g. convert to a line chart / show top 5 categories / add a total column / change to weekly aggregation"
            />
            <div className="text-[10px] text-[color:var(--ink-3)] italic">
              Current type: <b>{widget.type}</b>
              {widget.dataset_id && <> · single-source</>}
              {widget.federated_spec && <> · cross-source ({widget.federated_spec?.subqueries?.length || 0} subqueries)</>}
            </div>
            {error && (
              <div className="text-[11px] text-[color:var(--danger)] flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />{error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid={`wiz-refine-apply-${idx}`} onClick={refine} disabled={busy || !instruction.trim()}>
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Apply refine
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

